import {Component, ElementRef, inject, output, OutputEmitterRef} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DsButtonModule, DsFormFieldModule, DsLabelModule, DsLoadingModule, DsMessageModule} from '@bmw-ds/components';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Customer, CustomerWithVehicles, Vehicle} from '@service-and-repairs/calm-leads-customer-lib-api';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, GridApi, GridReadyEvent} from 'ag-grid-community';
import {AnalyticsService} from '../../../core/analytics/services/analytics.service';
import {CustomerLoader} from '../../../core/customer/services/customer.loader';
import {DmsVehicle} from '../dms-vehicle';
import {CustomerSearchColumnDefinitions} from './customer-search-column-definitions';
import {CustomerSourcePipe} from './customer-source.pipe';

@Component({
  selector: 'app-customer-search',
  templateUrl: './customer-search.component.html',
  styleUrls: ['./customer-search.component.scss'],
  imports: [
    DsFormFieldModule,
    DsLabelModule,
    FormsModule,
    DsButtonModule,
    DsLoadingModule,
    AgGridAngular,
    DsMessageModule,
    TranslateModule,
    CustomerSourcePipe
  ]
})
export class CustomerSearchComponent {
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly customerLoader: CustomerLoader = inject(CustomerLoader);

  readonly selectedVehicle: OutputEmitterRef<DmsVehicle> = output<DmsVehicle>();

  firstName: string;
  lastName: string;

  showLoadingIndicator: boolean = false;
  searchResult: CustomerWithVehicles[];
  errorMessage: string;

  vehicleColumnDefinitions: ColDef<Vehicle>[];
  defaultColumnDefinition: ColDef<Vehicle>;
  private gridApi: GridApi<Vehicle>;

  constructor() {
    this.defaultColumnDefinition = CustomerSearchColumnDefinitions.getDefaultVehicleColumnDefinition();
    this.vehicleColumnDefinitions = CustomerSearchColumnDefinitions.getVehicleColumnDefinitions(this.translate);
  }

  onGridReady(event: GridReadyEvent<Vehicle, Vehicle>): void {
    this.gridApi = event.api;
  }

  resizeColumns(): void {
    if (this.elementVisible()) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  searchCustomer(): void {
    this.searchResult = undefined;
    this.errorMessage = undefined;
    this.showLoadingIndicator = true;
    this.customerLoader.getCustomersByName(this.firstName, this.lastName)
      .then((customers: CustomerWithVehicles[]): CustomerWithVehicles[] => this.searchResult = customers)
      .catch((error: Error): string => this.errorMessage = error.message)
      .finally((): boolean => this.showLoadingIndicator = false);
    this.analyticsService.postDmsRequestEvent('customer-search');
  }

  selectVehicle(vehicle: Vehicle, customer: Customer): void {
    this.selectedVehicle.emit({
      customerId: customer.idCollection.dmsId,
      customerFirstName: customer.nameCollection?.firstName,
      customerLastName: customer.nameCollection?.lastName,
      vin: vehicle.vin,
      licensePlate: vehicle.numberPlate
    });
  }

  private elementVisible(): boolean {
    // element is visible if it has an offset parent
    return !!this.elementRef.nativeElement.offsetParent;
  }
}
